// @flow
/* eslint-disable flowtype/no-weak-types,no-duplicate-imports */
import * as React from 'react';
import { get, isEmpty } from 'lodash';
import classnames from 'classnames';

import ReactAppContext from 'Context/ReactAppContext';
import * as routeBuilder from 'Routes/routeBuilder';
import BookingUtils from 'Utils/ple-utils/bookingUtils';
import { Product } from 'Components/widgets/product/Product.jsx';
import { getProductBadgeIcon } from 'Components/utils/product/product.utils';
import BookingPaymentStatus from 'Components/widgets/booking-payment-status/BookingPaymentStatus.jsx';
import TravelDetails from 'Components/widgets/travel-details/TravelDetails.jsx';
import { EmptyTripOverview } from 'Components/widgets/trip-summary/empty-trip-overview/EmptyTripOverview.jsx';

import { track } from 'Components/widgets/trip-summary/TripSummary.tracking';
import type { ProductProps } from 'Components/widgets/product/Product.utils';

type trackingProps = {
  categoryGA: string,
  actionGA: string,
  labelGA: string,
};

type Props = {
  className?: string,
  +i18n: {
    +memberSpace: *,
  },
  stay: Stay,
  emptyTripOverview: {
    text: string,
    link?: string,
    linkLabel?: string,
  },
  children?: React.Node,
  trackingData?: {
    destinationLink?: trackingProps,
  },
};

function createPropsForPictures(images) {
  if (!Array.isArray(images)) {
    return [];
  }

  return images.map(link => {
    return { src: link };
  });
}

export function createPropsForProduct(
  resort: $Shape<Resort>,
  tracking: ?trackingProps,
  appContext: AppContext,
): ProductProps {
  const badgeIcon = {
    comfort: resort.comfort,
    badgeIcon: resort.badgeIcon,
    type: resort.type,
  };

  return {
    id: resort.id,
    header: {
      className: 'border-r-1',
      badgeIcon: { ...getProductBadgeIcon(badgeIcon) },
      heading: {
        subtitle: get(resort, 'destination.country'),
        title: resort.name || '',
      },
      link: {
        url: resort.href,
        onClick: () => track(tracking, appContext),
      },
      productId: resort.id,
      productType: resort.type,
    },
    thumbnail: {
      link: {
        url: resort.href,
      },
      pictures: createPropsForPictures(resort.images),
      productId: resort.id,
      productType: resort.type,
    },
    url: resort.href,
    type: resort.type || '',
  };
}

export function TripOverview({
  className,
  i18n,
  stay,
  emptyTripOverview,
  children,
  trackingData = {},
}: Props) {
  const appContext = React.useContext(ReactAppContext);
  if (isEmpty(stay)) {
    return (
      <EmptyTripOverview
        className={className}
        text={emptyTripOverview.text}
        link={emptyTripOverview.link}
        linkLabel={emptyTripOverview.linkLabel}
      />
    );
  }

  if (!stay.resort) return null;

  return (
    <div
      className={classnames('TripOverview', 'flex flex-col justify-between border-1', className)}
    >
      <Product
        {...createPropsForProduct(stay.resort, trackingData.destinationLink, appContext)}
        className="-m-px"
      />
      <div className="flex flex-col justify-between flex-grow p-4">
        {!BookingUtils.isPastTrip(stay.returnDate) ? (
          <BookingPaymentStatus
            className="w-full"
            dataSelector="TripOverview-BookingPaymentStatus"
            isBookingPaid={stay.isPaid}
            shouldProposeOnlinePayment={stay.shouldProposeOnlinePayment}
            status={stay.bookingStatus}
            bookingPaidLabel={get(i18n, 'memberSpace.trips.price.stayPaidLabel')}
            bookingStatusLink={{
              label: get(i18n, 'memberSpace.trips.completePaymentLabel'),
              url: `${routeBuilder.buildUrl('bookings', {
                bookingId: stay.bookingId,
                stayId: stay.id,
              })}#price-and-payment`,
            }}
          />
        ) : null}
        <TravelDetails
          className="w-full mt-4"
          config={i18n}
          bookingStatus={stay.bookingStatus}
          stay={stay}
          isVertical
        />
        {children}
      </div>
    </div>
  );
}
